import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Zap, Gift, ArrowRight, Clock, Flame, Star } from "lucide-react";
import { PAGE_NAME } from "../../types";

interface ReferralBoosterModalProps {
  isOpen: boolean;
  onClose: () => void;
  endTime?: Date; // When the boost expires
}

const ReferralBoosterModal: React.FC<ReferralBoosterModalProps> = ({ 
  isOpen, 
  onClose,
  endTime = new Date(Date.now() + 24 * 60 * 60 * 1000) // Default 24h from now
}) => {
  const navigate = useNavigate();
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const calcTimeLeft = () => {
      const diff = endTime.getTime() - Date.now();
      if (diff <= 0) {
        return { hours: 0, minutes: 0, seconds: 0 };
      }
      return {
        hours: Math.floor(diff / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000),
      };
    };

    setTimeLeft(calcTimeLeft());

    const timer = setInterval(() => {
      setTimeLeft(calcTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [endTime]);

  const handleInvite = () => {
    onClose();
    navigate(PAGE_NAME.INVITE_USERS);
  };

  const formatNum = (n: number) => n.toString().padStart(2, '0');

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.8, opacity: 0, y: 20 }}
          transition={{ type: "spring", damping: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-sm relative"
        >
          {/* Glow Effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-yellow-500 via-red-500 to-pink-500 rounded-3xl blur-xl opacity-50 animate-pulse" />
          
          <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-3xl p-6 border border-yellow-500/30 shadow-2xl overflow-hidden">
            {/* Animated Background */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute w-40 h-40 bg-yellow-500/20 rounded-full blur-3xl -top-10 -right-10 animate-pulse" />
              <div className="absolute w-32 h-32 bg-red-500/20 rounded-full blur-2xl bottom-0 left-0" />
            </div>

            {/* Close Button */}
            <button 
              onClick={onClose}
              className="absolute top-3 right-3 p-1.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors z-20"
            >
              <X size={14} className="text-white/70" />
            </button>

            {/* Badge */}
            <div className="relative z-10 flex justify-center mb-4">
              <motion.div 
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ repeat: Infinity, duration: 2 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-red-600 to-orange-500 text-white text-sm font-bold shadow-lg shadow-red-500/30"
              >
                <Flame size={16} className="animate-pulse" />
                ⚡ LIMITED TIME ⚡
              </motion.div>
            </div>

            {/* Main Reward */}
            <div className="relative z-10 text-center mb-4">
              <p className="text-white/60 text-sm mb-1">Referral rewards boosted to</p>
              <div className="flex items-center justify-center gap-3">
                <span className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400">
                  400
                </span>
                <div className="flex flex-col items-start">
                  <span className="text-2xl font-bold text-white">ATH</span>
                  <span className="text-xs text-emerald-400 font-bold">per friend!</span>
                </div>
              </div>
              <div className="flex items-center justify-center gap-1 mt-2">
                <span className="text-white/40 text-sm line-through">200 ATH</span>
                <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-xs font-bold">2X BONUS</span>
              </div>
            </div>

            {/* Countdown Timer */}
            <div className="relative z-10 bg-black/30 rounded-2xl p-4 mb-4 border border-white/10">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Clock size={14} className="text-red-400" />
                <span className="text-[10px] text-red-400 font-bold uppercase tracking-wider">Offer Ends In</span>
              </div>
              <div className="flex justify-center items-center gap-2">
                <div className="bg-red-500/20 px-3 py-2 rounded-xl">
                  <span className="text-2xl font-black text-white font-mono">{formatNum(timeLeft.hours)}</span>
                  <p className="text-[8px] text-white/40 uppercase">Hours</p>
                </div>
                <span className="text-2xl font-bold text-white/30">:</span>
                <div className="bg-red-500/20 px-3 py-2 rounded-xl">
                  <span className="text-2xl font-black text-white font-mono">{formatNum(timeLeft.minutes)}</span>
                  <p className="text-[8px] text-white/40 uppercase">Mins</p>
                </div>
                <span className="text-2xl font-bold text-white/30">:</span>
                <div className="bg-red-500/20 px-3 py-2 rounded-xl">
                  <span className="text-2xl font-black text-white font-mono animate-pulse">{formatNum(timeLeft.seconds)}</span>
                  <p className="text-[8px] text-white/40 uppercase">Secs</p>
                </div>
              </div>
            </div>

            {/* Extra Perks */}
            <div className="relative z-10 grid grid-cols-2 gap-2 mb-4">
              <div className="flex items-center gap-2 bg-white/5 rounded-xl p-2">
                <Star size={14} className="text-yellow-400" />
                <span className="text-[10px] text-white/70">+20% Tier 2 Commission</span>
              </div>
              <div className="flex items-center gap-2 bg-white/5 rounded-xl p-2">
                <Gift size={14} className="text-purple-400" />
                <span className="text-[10px] text-white/70">Friends Get 200 ATH!</span>
              </div>
            </div>

            {/* CTA Button */}
            <motion.button
              onClick={handleInvite}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="relative z-10 w-full py-4 rounded-2xl bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 text-white font-black text-lg flex items-center justify-center gap-2 shadow-lg shadow-orange-500/40"
            >
              <Zap size={20} />
              Invite Now & Earn 2X
              <ArrowRight size={18} />
            </motion.button>

            {/* Urgency Text */}
            <p className="relative z-10 text-center text-[10px] text-white/40 mt-3">
              ⚠️ This bonus won't come back soon. Don't miss it!
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ReferralBoosterModal;
